import { useEffect } from 'react'
import { createFileRoute, redirect, useRouterState } from '@tanstack/react-router'
import { KnowledgeShell } from '../../../components/knowledge/KnowledgeShell'
import { useCollections } from '../../../hooks/useCollections'
import { workspacesQueryOptions } from '../../../hooks/useWorkspaces'
import type { Workspace } from '../../../lib/api'
import { authClient } from '../../../lib/auth-client'
import { setLastWorkspaceId } from '../../../lib/last-workspace'
import { isPublicKbHost } from '../../../lib/public-kb-host'
import { useKnowledgeEditorStore } from '../../../stores/knowledgeEditorStore'

export const Route = createFileRoute('/knowledge/$workspaceId')({
  beforeLoad: async ({ params, context }) => {
    if (isPublicKbHost()) {
      throw redirect({ to: '/knowledge' })
    }

    const { data: session } = await authClient.getSession()
    if (!session) {
      throw redirect({ to: '/login' })
    }

    const workspacesResult = await context.queryClient.ensureQueryData(
      workspacesQueryOptions(),
    )
    if (!workspacesResult.success) {
      throw redirect({ to: '/' })
    }

    const workspace = workspacesResult.data.find(
      (w: Workspace) => w.id === params.workspaceId,
    )
    if (!workspace || !workspace.modules.includes('knowledge')) {
      throw redirect({ to: '/' })
    }

    return { workspace, session }
  },
  component: TeamKnowledgeLayout,
})

function TeamKnowledgeLayout() {
  const { workspaceId } = Route.useParams()
  const { data: collections = [] } = useCollections(workspaceId)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const segments = pathname.split('/').filter(Boolean)
  const activeArticleId = segments.length >= 3 ? segments[2] : undefined

  useEffect(() => {
    setLastWorkspaceId(workspaceId)
  }, [workspaceId])

  useEffect(() => () => useKnowledgeEditorStore.getState().clearActions(), [])

  return (
    <KnowledgeShell
      workspaceId={workspaceId}
      activeArticleId={activeArticleId}
      collections={collections}
    />
  )
}
